import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, DatePicker, Space, Spin, Alert, Tag } from 'antd';
import Table from 'components/Common/Table';
import { ArrowUpOutlined, ArrowDownOutlined, TeamOutlined, WalletOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart, Bar, Line } from 'recharts';
import axios from 'axios';
import useIsMobile from '../../hooks/useIsMobile';
import ReportFilterModal, { DatePresetButtons, MobileFilterButton, getDateRangeFromPreset } from '../../components/Reports/ReportFilterModal';
import { categoryFill, categoryLabel } from '../../utils/categoryColors';
import { expenseTypeOf } from '../../utils/expenseTypes';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import useUrlFilters from '../../hooks/useUrlFilters';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const COLORS = ['#ff4d4f', '#fa8c16', '#faad14', '#52c41a', '#1890ff', '#722ed1', '#eb2f96', '#13c2c2'];

// Filters live in the URL so a filtered report can be shared as a link.
// Relative presets are kept as the preset name; only custom and FY ranges carry dates.
const URL_FILTERS = {
  preset: { type: 'string', default: 'all' },
  range: { type: 'dateRange', keys: ['start_date', 'end_date'] },
};
const PRESETS_WITH_DATES = ['custom', 'this_fy', 'last_fy'];
// Relative presets are recomputed from the name; a custom range with no dates is empty.
const resolveRange = (preset, range) =>
  (PRESETS_WITH_DATES.includes(preset) && range) || (preset === 'custom' ? null : getDateRangeFromPreset(preset));

const ProfitLossReport = () => {
  const isMobile = useIsMobile();
  const reportRef = useRef(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const filterPreset = filters.preset;
  const dateRange = useMemo(() => resolveRange(filterPreset, filters.range), [filterPreset, filters.range]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const useDateRange = filterPreset === 'custom' || filterPreset === 'this_fy' || filterPreset === 'last_fy';
      const params = {
        filter_preset: useDateRange ? undefined : (filterPreset === 'all' ? 'all_time' : filterPreset),
        start_date: useDateRange && dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: useDateRange && dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      };

      const response = await axios.get('/accounting/expense/profit-loss-analytics/', { params });

      if (response.status === 200) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to fetch profit/loss data');
    } finally {
      setLoading(false);
    }
  }, [filterPreset, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle preset change using helper
  const handlePresetChange = (preset) => {
    setFilters({
      preset,
      range: PRESETS_WITH_DATES.includes(preset) ? getDateRangeFromPreset(preset) : null,
    });
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({ preset: 'custom', range: [dates[0], dates[1]] });
    } else {
      setFilters({ preset: 'all', range: null });
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({ preset: 'all', range: null });
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filterPreset !== 'all') count++;
    return count;
  };

  // Prepare chart data
  const expenseBreakdownData = data?.expense_breakdown?.map((item, index) => ({
    name: categoryLabel(item),
    value: item.amount,
    fill: categoryFill(item, index, COLORS)
  })) || [];

  // Monthly breakdown table columns with responsive config
  const monthlyColumns = [
    { title: 'Month', dataIndex: 'month', key: 'month', width: 100 },
    { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', render: (v) => <Text style={{ color: '#52c41a' }}>₹{v?.toLocaleString('en-IN')}</Text>, align: 'right', width: 120 },
    { title: 'Total Expenses', dataIndex: 'expenses', key: 'expenses', render: (v) => <Text style={{ color: '#ff4d4f' }}>₹{v?.toLocaleString('en-IN')}</Text>, align: 'right', width: 120 },
    { title: 'Payroll', dataIndex: 'payroll', key: 'payroll', render: (v) => <Text style={{ color: '#1890ff' }}>₹{v?.toLocaleString('en-IN')}</Text>, align: 'right', width: 100, responsive: ['md'] },
    { title: 'Other Exp.', dataIndex: 'regular_expenses', key: 'regular_expenses', render: (v) => <Text style={{ color: '#fa8c16' }}>₹{v?.toLocaleString('en-IN')}</Text>, align: 'right', width: 100, responsive: ['lg'] },
    {
      title: 'Profit/Loss',
      dataIndex: 'profit',
      key: 'profit',
      render: (v) => (
        <Text style={{ color: v >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {v >= 0 ? '' : '-'}₹{Math.abs(v)?.toLocaleString('en-IN')}
        </Text>
      ),
      align: 'right',
      width: 120
    },
  ];

  const renderExpenseType = (expenseType) => {
    const { label, color } = expenseTypeOf(expenseType);
    return <Tag color={color}>{label}</Tag>;
  };

  // Expense breakdown table columns
  const expenseColumns = [
    { title: 'Category', dataIndex: 'category_name', key: 'category', render: (name, record) => <Tag color={record.category_color || 'default'}>{name || 'Uncategorized'}</Tag> },
    { title: 'Expense Type', dataIndex: 'expense_type', key: 'expense_type', render: renderExpenseType },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v) => `₹${v?.toLocaleString('en-IN')}`, align: 'right' },
    { title: 'Share', dataIndex: 'percentage', key: 'percentage', render: (v) => `${v}%`, align: 'right' },
  ];

  // Expense type (variable / non-variable) breakdown table columns
  const expenseTypeColumns = [
    { title: 'Expense Type', dataIndex: 'expense_type', key: 'expense_type', render: renderExpenseType },
    { title: 'Count', dataIndex: 'count', key: 'count', align: 'right' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v) => `₹${v?.toLocaleString('en-IN')}`, align: 'right' },
    { title: 'Share', dataIndex: 'percentage', key: 'percentage', render: (v) => `${v}%`, align: 'right' },
  ];

  // Tables for the PDF export. Passed explicitly rather than scraped from the
  // rendered tables so the export carries every row, not just the page on screen.
  const pdfTables = [
    {
      title: 'Monthly Breakdown',
      columns: [
        { header: 'Month', key: 'month' },
        { header: 'Revenue', key: 'revenue', align: 'right', format: 'currency' },
        { header: 'Total Expenses', key: 'expenses', align: 'right', format: 'currency' },
        { header: 'Payroll', key: 'payroll', align: 'right', format: 'currency' },
        { header: 'Other Expenses', key: 'regular_expenses', align: 'right', format: 'currency' },
        { header: 'Variable', key: 'variable_expenses', align: 'right', format: 'currency' },
        { header: 'Non-Variable', key: 'non_variable_expenses', align: 'right', format: 'currency' },
        { header: 'Profit/Loss', key: 'profit', align: 'right', format: 'currency' },
      ],
      rows: data?.monthly_trend || [],
    },
    {
      title: 'Expense Type Breakdown',
      columns: [
        { header: 'Expense Type', key: 'expense_type_display' },
        { header: 'Count', key: 'count', align: 'right' },
        { header: 'Amount', key: 'amount', align: 'right', format: 'currency' },
        { header: 'Share', key: 'percentage', align: 'right', format: 'percent' },
      ],
      rows: data?.expense_type_breakdown || [],
    },
    {
      title: 'Expense Category Breakdown',
      columns: [
        { header: 'Category', key: 'category_name' },
        { header: 'Expense Type', key: 'expense_type_display' },
        { header: 'Amount', key: 'amount', align: 'right', format: 'currency' },
        { header: 'Share', key: 'percentage', align: 'right', format: 'percent' },
      ],
      rows: data?.expense_breakdown || [],
    },
  ];

  const pdfMeta = [
    { label: 'Revenue', value: `Rs. ${(data?.summary?.total_revenue || 0).toLocaleString('en-IN')}` },
    { label: 'Expenses', value: `Rs. ${(data?.summary?.total_expenses || 0).toLocaleString('en-IN')}` },
    { label: 'Variable', value: `Rs. ${(data?.summary?.total_variable_expenses || 0).toLocaleString('en-IN')}` },
    { label: 'Non-Variable', value: `Rs. ${(data?.summary?.total_non_variable_expenses || 0).toLocaleString('en-IN')}` },
    { label: data?.summary?.is_profitable ? 'Net Profit' : 'Net Loss', value: `Rs. ${Math.abs(data?.summary?.net_profit || 0).toLocaleString('en-IN')}` },
    { label: 'Profit Margin', value: `${data?.summary?.profit_margin || 0}%` },
  ];

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>Profit & Loss Report</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ExportPdfButton
            reportRef={reportRef}
            title="Profit & Loss Report"
            subtitle={data?.summary?.date_range}
            meta={pdfMeta}
            tables={pdfTables}
            fileName="profit-and-loss-report"
            disabled={loading || !data}
          />
          {isMobile && (
            <MobileFilterButton
              onClick={() => setFilterModalVisible(true)}
              activeFiltersCount={getActiveFiltersCount()}
            />
          )}
        </div>
      </div>

      {/* Desktop Filters */}
      {!isMobile && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Date Range:</Text>
              <DatePresetButtons filterPreset={filterPreset} onPresetChange={handlePresetChange} />
            </div>
            <Space wrap size="middle">
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Custom Date Range:</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  size="small"
                  allowClear={true}
                  placeholder={['Start Date', 'End Date']}
                />
              </div>
            </Space>
          </Space>
        </Card>
      )}

      {/* Mobile Filter Modal */}
      <ReportFilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        filterPreset={filterPreset}
        dateRange={dateRange}
        onChange={({ preset, range }) => setFilters({
          preset,
          range: PRESETS_WITH_DATES.includes(preset) ? range : null,
        })}
        onClearFilters={handleClearFilters}
        title="Profit & Loss Filters"
      />

      {error && (
        <Alert message="Error" description={error} type="error" closable showIcon onClose={() => setError(null)} style={{ marginBottom: '16px' }} />
      )}

      {loading && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}><Text>Loading profit/loss data...</Text></div>
          </div>
        </Card>
      )}

      {!loading && data && (
        <>
          {/* Summary Statistics */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Revenue</span>}
                  value={data.summary?.total_revenue || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px', color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Expenses</span>}
                  value={data.summary?.total_expenses || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px', color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }} style={{ borderLeft: data.summary?.is_profitable ? '4px solid #52c41a' : '4px solid #ff4d4f' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>{data.summary?.is_profitable ? "Net Profit" : "Net Loss"}</span>}
                  value={Math.abs(data.summary?.net_profit || 0)}
                  prefix={data.summary?.is_profitable ? <><ArrowUpOutlined /> ₹</> : <><ArrowDownOutlined /> ₹</>}
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px', color: data.summary?.is_profitable ? '#52c41a' : '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Profit Margin</span>}
                  value={data.summary?.profit_margin || 0}
                  suffix="%"
                  precision={1}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px', color: data.summary?.profit_margin >= 0 ? '#52c41a' : '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>

          {/* P&L Summary Card */}
          <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Profit & Loss Summary</Text>} style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div style={{ padding: isMobile ? '12px' : '16px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
                  <Row>
                    <Col span={12}>
                      <Text strong style={{ fontSize: isMobile ? '13px' : '16px' }}>Revenue (Income)</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                      <Text strong style={{ fontSize: isMobile ? '13px' : '16px', color: '#52c41a' }}>₹{data.summary?.total_revenue?.toLocaleString('en-IN')}</Text>
                    </Col>
                  </Row>
                  <div style={{ borderTop: '1px dashed #d9d9d9', marginTop: '12px', paddingTop: '12px' }}>
                    <Row>
                      <Col span={12}>
                        <Text strong style={{ fontSize: isMobile ? '13px' : '16px' }}>Less: Expenses</Text>
                      </Col>
                      <Col span={12} style={{ textAlign: 'right' }}>
                        <Text strong style={{ fontSize: isMobile ? '13px' : '16px', color: '#ff4d4f' }}>₹{data.summary?.total_expenses?.toLocaleString('en-IN')}</Text>
                      </Col>
                    </Row>
                    <Row style={{ marginTop: '4px', paddingLeft: isMobile ? '8px' : '16px' }}>
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '14px' }}><TeamOutlined style={{ marginRight: 4 }} />Payroll</Text>
                      </Col>
                      <Col span={12} style={{ textAlign: 'right' }}>
                        <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '14px' }}>₹{data.summary?.total_payroll?.toLocaleString('en-IN')}</Text>
                      </Col>
                    </Row>
                    <Row style={{ marginTop: '4px', paddingLeft: isMobile ? '8px' : '16px' }}>
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '14px' }}><WalletOutlined style={{ marginRight: 4 }} />Other Expenses</Text>
                      </Col>
                      <Col span={12} style={{ textAlign: 'right' }}>
                        <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '14px' }}>₹{data.summary?.total_regular_expenses?.toLocaleString('en-IN')}</Text>
                      </Col>
                    </Row>
                  </div>
                  <div style={{ borderTop: '2px solid #d9d9d9', marginTop: '12px', paddingTop: '12px' }}>
                    <Row>
                      <Col span={12}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '18px' }}>{data.summary?.is_profitable ? 'Net Profit' : 'Net Loss'}</Text>
                      </Col>
                      <Col span={12} style={{ textAlign: 'right' }}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '18px', color: data.summary?.is_profitable ? '#52c41a' : '#ff4d4f' }}>
                          {data.summary?.is_profitable ? '' : '-'}₹{Math.abs(data.summary?.net_profit || 0).toLocaleString('en-IN')}
                        </Text>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            {/* Monthly Revenue vs Expenses Chart */}
            <Col xs={24} md={16}>
              <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Monthly Revenue vs Expenses</Text>}>
                {data.monthly_trend && data.monthly_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 320}>
                    <ComposedChart data={data.monthly_trend} margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: isMobile ? 9 : 11 }} angle={-45} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} width={isMobile ? 50 : 60} />
                      <Tooltip formatter={(value) => `₹${value?.toLocaleString('en-IN')}`} />
                      <Legend wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }} />
                      <Bar dataKey="revenue" fill="#52c41a" name="Revenue" />
                      <Bar dataKey="expenses" fill="#ff4d4f" name="Expenses" />
                      <Line type="monotone" dataKey="profit" stroke="#1890ff" strokeWidth={2} name="Profit" dot={{ r: isMobile ? 3 : 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}><Text type="secondary">No monthly data available</Text></div>
                )}
              </Card>
            </Col>

            {/* Expense Breakdown Pie Chart */}
            <Col xs={24} md={8}>
              <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Expense Breakdown</Text>}>
                {expenseBreakdownData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 320}>
                    <PieChart>
                      <Pie
                        data={expenseBreakdownData}
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? 35 : 50}
                        outerRadius={isMobile ? 70 : 90}
                        paddingAngle={2}
                        dataKey="value"
                        label={isMobile ? false : ({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={!isMobile && { length: 10, length2: 5 }}
                      >
                        {expenseBreakdownData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `₹${value?.toLocaleString('en-IN')}`} />
                      {isMobile && <Legend wrapperStyle={{ fontSize: '10px' }} />}
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}><Text type="secondary">No expense breakdown available</Text></div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Monthly Details Table */}
          {data.monthly_trend && data.monthly_trend.length > 0 && (
            <Card size="small" title={
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Monthly Breakdown</Text>
                {!isMobile && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>{data.summary?.date_range}</Text>
                )}
              </div>
            } style={{ marginBottom: '16px' }}>
              <Table
                dataSource={data.monthly_trend}
                rowKey="month"
                size="small"
                pagination={{
                  defaultPageSize: isMobile ? 5 : 10,
                  pageSizeOptions: ['5', '10', '20'],
                  showSizeChanger: !isMobile,
                  simple: isMobile,
                  hideOnSinglePage: data.monthly_trend.length <= (isMobile ? 5 : 10),
                }}
                scroll={{ x: 'max-content' }}
                columns={monthlyColumns}
                summary={(pageData) => {
                  const totalRevenue = pageData.reduce((sum, row) => sum + (row.revenue || 0), 0);
                  const totalExpenses = pageData.reduce((sum, row) => sum + (row.expenses || 0), 0);
                  const totalPayroll = pageData.reduce((sum, row) => sum + (row.payroll || 0), 0);
                  const totalRegularExpenses = pageData.reduce((sum, row) => sum + (row.regular_expenses || 0), 0);
                  const totalProfit = pageData.reduce((sum, row) => sum + (row.profit || 0), 0);
                  return (
                    <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                      <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right"><Text strong style={{ color: '#52c41a' }}>₹{totalRevenue?.toLocaleString('en-IN')}</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right"><Text strong style={{ color: '#ff4d4f' }}>₹{totalExpenses?.toLocaleString('en-IN')}</Text></Table.Summary.Cell>
                      {!isMobile && (
                        <>
                          <Table.Summary.Cell index={3} align="right"><Text strong style={{ color: '#1890ff' }}>₹{totalPayroll?.toLocaleString('en-IN')}</Text></Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right"><Text strong style={{ color: '#fa8c16' }}>₹{totalRegularExpenses?.toLocaleString('en-IN')}</Text></Table.Summary.Cell>
                        </>
                      )}
                      <Table.Summary.Cell index={isMobile ? 3 : 5} align="right">
                        <Text strong style={{ color: totalProfit >= 0 ? '#52c41a' : '#ff4d4f' }}>
                          {totalProfit >= 0 ? '' : '-'}₹{Math.abs(totalProfit)?.toLocaleString('en-IN')}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Card>
          )}

          {/* Expense Type Breakdown Table */}
          {data.expense_type_breakdown && data.expense_type_breakdown.length > 0 && (
            <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Expense Type Breakdown</Text>} style={{ marginTop: '16px' }}>
              <Table
                dataSource={data.expense_type_breakdown}
                rowKey="expense_type"
                size="small"
                pagination={false}
                columns={expenseTypeColumns}
              />
            </Card>
          )}

          {/* Expense Breakdown Table */}
          {data.expense_breakdown && data.expense_breakdown.length > 0 && (
            <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Expense Category Breakdown</Text>} style={{ marginTop: '16px' }}>
              <Table
                dataSource={data.expense_breakdown}
                rowKey="category"
                size="small"
                pagination={{
                  defaultPageSize: isMobile ? 5 : 10,
                  showSizeChanger: !isMobile,
                  simple: isMobile,
                  hideOnSinglePage: data.expense_breakdown.length <= (isMobile ? 5 : 10),
                }}
                columns={expenseColumns}
              />
            </Card>
          )}
        </>
      )}

      {!loading && !data && !error && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">No profit/loss data available.</Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ProfitLossReport;
